/**
 * world.ts — The ground the town stands on: ground, parks, roads, footways,
 * trees, the bin, and the home zone.
 *
 * Everything here is built from the same config the gameplay uses, so the 3D city
 * and the collision data can't drift apart. Ground-plane rects from config are
 * mapped as world `(x, y)` → Three `(x, height, y)`.
 *
 * The **buildings** themselves live next door in `buildings.ts` — they carry
 * enough detail now (roofline, dormers, facades) to be their own module, and
 * keeping them apart is what stops this file being half houses.
 */
import * as THREE from 'three';
import {
  GROUND,
  LANES,
  PARK_FURNITURE,
  PAVEMENT,
  ROADS,
  OBJECTIVE,
  TREES,
  YARDS,
  BUILDINGS,
} from '../config/constants';
import { activeFootways, activeLevel } from '../config/level';
import { FOLIAGE } from '../config/palette';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {
  clayGeometry,
  clayPill,
  clayMaterial,
  claySlab,
  claySphere,
  claySphereGeometry,
  clayWobble,
  texturedSlab,
  updateBoundsUniform,
} from './clay';
import { Instances } from './instances';
import { COBBLE_CELL, PAVING_CELL, cobbleTexture, masonryMaterial, pavingTexture } from './masonry';
import {
  barkTexture,
  grassTexture,
  paintPlane,
  patternedClay,
  scrubTexture,
  tarmacTexture,
} from './textures';
import { buildBuildings, buildCottage } from './buildings';
import {
  createFlowerBed,
  createParkBench,
  createParkFountain,
  createPhoneBox,
  createPostBox,
} from './models';
import { parkPath, planParkFurniture, type FurnitureKind } from '../systems/parkFurniture';
import { homeHouseRect } from '../systems/home';
import { blobShade, canopyBlobs, canopyBottom, canopyTop } from '../systems/trees';
import { hash2 } from '../utils/math';
import { shadeHex } from '../utils/color';
import { frontFacing, patioRect, pointInAnyRect, rectCenter, type Rect } from '../systems/level';
import {
  blockInteriors,
  perimeterTufts,
  freeRects,
  scatter,
  type KeepOut,
  type Plant,
} from '../systems/yards';
import { footwayEndCaps, laneBands, subtractGaps } from '../systems/pavement';
import {
  carriagewayRects,
  carriagewayWidth,
  crossingGaps,
  isStreet,
  roadSpan,
} from '../systems/roads';
import {
  findJunctions,
  liveApproaches,
  stopLines,
} from '../systems/junctions';
import { drainPositions, kerbLines, zebraStripes } from '../systems/markings';

/**
 * The ground's shared patterned materials, built on first use.
 *
 * Lazily, because generating one draws on a canvas — fine in the browser, but it
 * would run at import time in any test that so much as mentions this module.
 */
let tarmacMat: THREE.Material | undefined;
let pavingMat: THREE.Material | undefined;

const tarmac = (): THREE.Material =>
  (tarmacMat ??= patternedClay(ROADS.COLOR, tarmacTexture(ROADS.COLOR), ROADS.AGGREGATE_RELIEF));

const paving = (): THREE.Material =>
  (pavingMat ??= masonryMaterial(
    PAVEMENT.COLOR,
    pavingTexture(PAVEMENT.COLOR),
    PAVEMENT.PAVING_RELIEF,
  ));

/**
 * A flat ground surface carrying a repeating pattern at a fixed world size.
 *
 * The counterpart to the walls' `courseGeometry`: the plane is built fresh (so
 * its UVs can be scaled without touching any other mesh's) and the pattern's
 * repeat count comes from the surface's real extent. See `render/textures.ts`
 * for why the count is baked into the geometry rather than set on the texture.
 */
function patternedGround(
  w: number,
  d: number,
  material: THREE.Material,
  cell: number,
  cx: number,
  y: number,
  cz: number,
): THREE.Mesh {
  const geo = new THREE.PlaneGeometry(w, d);
  paintPlane(geo, w, d, cell);
  return texturedSlab(geo, material, cx, y, cz);
}

/**
 * Build all static scenery and add it to the scene.
 * Layers are stacked in tiny y increments to avoid z-fighting between coplanar
 * surfaces (pavement → parks → tarmac → markings).
 */
export function buildWorld(scene: THREE.Scene): void {
  updateBoundsUniform();
  buildGround(scene);
  buildRoads(scene);
  buildJunctions(scene);
  buildFootways(scene);
  buildBuildings(scene);
  buildHome(scene);
  buildYards(scene);
  buildBoundaryHedge(scene);
  buildBins(scene);
  buildTrees(scene);
  buildLandmarkProps(scene);
}

/** The models, in the order `systems/parkFurniture.ts` names them. */
const FURNITURE: Record<FurnitureKind, () => THREE.Group> = {
  fountain: createParkFountain,
  bench: createParkBench,
  kiosk: createPhoneBox,
  postbox: createPostBox,
  bed: createFlowerBed,
};

/**
 * Stand the park furniture on the greens.
 *
 * The judgement — which green, which piece, whereabouts on it — is
 * `planParkFurniture`'s; this only builds what it's handed. Two things are this
 * side of the line because they're about the *drawing*:
 *
 * - **Everything sits on the turf**, not on the ground plane. A green is raised
 *   on its kerb (see `buildGround`), so a prop placed at y=0 has its feet buried
 *   in the grass and its shadow under the surface.
 * - **No yaw beyond the wobble.** Each model carries what identifies it on its
 *   crown and on the +z face — the flank this camera can see — so turning one
 *   round is turning its front away from the player. The clay wobble is a
 *   degree or so, which is hand-placed rather than turned.
 */
function buildLandmarkProps(scene: THREE.Scene): void {
  const level = activeLevel();
  const pieces = planParkFurniture({
    parks: level.parks,
    bounds: level.bounds,
    keepOut: [...level.bins, level.start],
    clearance: YARDS.OBJECTIVE_CLEARANCE,
    minPark: PARK_FURNITURE.MIN_PARK,
    fountainPark: PARK_FURNITURE.FOUNTAIN_PARK,
    inset: PARK_FURNITURE.EDGE_INSET,
    pathWidth: PARK_FURNITURE.PATH_WIDTH,
    twoBenchPark: PARK_FURNITURE.TWO_BENCH_PARK,
  });

  for (const piece of pieces) {
    const prop = FURNITURE[piece.kind]();
    prop.name = `${FURNITURE_GROUP}-${piece.kind}`;
    prop.position.set(piece.x, turfTop(), piece.y);
    clayWobble(prop, piece.x, piece.y);
    scene.add(prop);
  }
}

/** Prefix on every piece of park furniture's group, so tests can find them. */
export const FURNITURE_GROUP = 'furniture';

/** The height of a park's mown surface — see `buildGround`. */
function turfTop(): number {
  return GROUND.PARK_KERB_HEIGHT + TURF_LIFT;
}

/** How far the turf floats over its kerb, so the two can't z-fight. */
const TURF_LIFT = 0.1;

/**
 * A hedge along the edge of the walk, where there is nothing else to stop you.
 *
 * The walker is clamped to `LevelSpec.bounds` while the town carries on past it
 * (see `levelgen/surround.ts`), and an invisible wall across open ground is the
 * one thing that would give the whole device away. Most of the boundary needs
 * nothing: it runs along the back of the terrace fronting the perimeter street
 * from the outside, and a house is its own explanation. This fills the gaps
 * between those houses with the same garden hedge the yards are full of, so
 * what stops the walker is always something they can see.
 *
 * **Not across the streets.** Every through-street crosses the boundary, and a
 * hedge growing across a carriageway would be a far stranger sight than the
 * clamp it was meant to hide. Those crossings are left alone deliberately: the
 * boundary sits on the *far kerb of the perimeter street*, so a player walking
 * out of town up a side street is stopped at the junction with the last road in
 * it — which is at least somewhere a walk plausibly ends.
 *
 * Does nothing on a level with no surround, where the bounds are the map and
 * the edge is already the end of the world.
 */
function buildBoundaryHedge(scene: THREE.Scene): void {
  const level = activeLevel();
  const { bounds, world } = level;
  if (bounds.w >= world.width && bounds.h >= world.height) return;

  const footways = activeFootways();
  const carriageways = carriagewayRects(world, level.roads, ROADS.WIDTH);
  const plots: Rect[] = level.buildings.map((b) => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
  const planting = new Instances();

  for (const edge of boundaryRuns(bounds)) {
    // Stepped along rather than scattered: this is a hedge on a boundary line,
    // and the one thing it must read as is a continuous run.
    const steps = Math.max(1, Math.round(edge.length / YARDS.BUSH_SPACING));
    for (let i = 0; i <= steps; i++) {
      const t = (i / steps) * edge.length;
      const x = edge.x + edge.dx * t + edge.nx * HEDGE_SETBACK;
      const y = edge.y + edge.dy * t + edge.ny * HEDGE_SETBACK;

      // Never on the paving or the tarmac, never inside a house that is already
      // doing this job, and never over anything the player is sent to find.
      if (pointInAnyRect(x, y, carriageways, YARDS.BUSH_RADIUS)) continue;
      if (pointInAnyRect(x, y, footways, YARDS.BUSH_RADIUS)) continue;
      if (pointInAnyRect(x, y, plots)) continue;
      if (level.bins.some((b) => Math.hypot(x - b.x, y - b.y) < YARDS.OBJECTIVE_CLEARANCE)) continue;
      if (Math.hypot(x - level.start.x, y - level.start.y) < YARDS.OBJECTIVE_CLEARANCE) continue;

      addBush(planting, { x, y, scale: 0.9 + hash2(x, y) * 0.3, yaw: hash2(y, x) * Math.PI * 2 });
    }
  }

  const hedge = new THREE.Group();
  hedge.name = GARDEN_GROUP;
  planting.build(hedge);
  scene.add(hedge);
}

/**
 * How far outside the boundary the hedge is planted.
 *
 * Outside, not on it. A bush is not solid, so this is about where it *looks*
 * like it grows: planted on the line, half of every bush would hang over the
 * footway the player is walking along and read as a hedge that needs cutting
 * back. Just clear of the line, it reads as the boundary of whatever is on the
 * other side — which is exactly what it is.
 */
const HEDGE_SETBACK = YARDS.BUSH_RADIUS;

/** The four edges of the walk, each as a start point, a direction and a normal. */
function boundaryRuns(
  b: Rect,
): { x: number; y: number; dx: number; dy: number; nx: number; ny: number; length: number }[] {
  return [
    { x: b.x, y: b.y, dx: 1, dy: 0, nx: 0, ny: -1, length: b.w }, // north
    { x: b.x, y: b.y + b.h, dx: 1, dy: 0, nx: 0, ny: 1, length: b.w }, // south
    { x: b.x, y: b.y, dx: 0, dy: 1, nx: -1, ny: 0, length: b.h }, // west
    { x: b.x + b.w, y: b.y, dx: 0, dy: 1, nx: 1, ny: 0, length: b.h }, // east
  ];
}

/** Name of the group every planted instance batch hangs under. */
export const GARDEN_GROUP = 'gardens';

/**
 * The gardens — the rough ground, hedges and weeds filling the spaces between
 * and behind the houses.
 *
 * Where all of it goes is decided by the pure `systems/yards.ts`; this only
 * turns the answers into meshes. That split is the same one `terrace.ts` and
 * `roofline.ts` get, and for the same reason: working out which ground is free
 * is a rectangle-subtraction problem with real edge cases, and those are worth
 * testing directly rather than eyeballing.
 *
 * Runs **after** `buildBuildings` so the ground it lays is guaranteed to be the
 * ground the houses left over — and before the bin and the trees, which stand
 * on it.
 */
function buildYards(scene: THREE.Scene): void {
  const scrub = patternedClay(
    YARDS.COLOR,
    scrubTexture(YARDS.COLOR, YARDS.SOIL_COLOR),
    YARDS.RELIEF,
  );

  // Ground that already belongs to something else. Note the home *plot* is not
  // here, only the house standing on it: cutting the whole rectangle out of the
  // garden left a grey slab sitting in the middle of a lawn, and the front
  // garden ought to look like everybody else's anyway.
  const level = activeLevel();
  const footways = activeFootways();
  const house = homeHouseRect(level.home);
  const plots: Rect[] = level.buildings.map((b) => ({ x: b.x, y: b.y, w: b.w, h: b.h }));
  const blockers: Rect[] = [
    ...plots,
    house,
    // Front patios. Their paving is raised, so this isn't about z-fighting —
    // it's that weeds coming up through somebody's front path is a bug.
    ...level.buildings
      .filter((b) => b.patio)
      .map((b) =>
        patioRect(
          { x: b.x, y: b.y, w: b.w, h: b.h },
          frontFacing({ x: b.x, y: b.y, w: b.w, h: b.h }, footways),
          BUILDINGS.PATIO_DEPTH,
        ),
      ),
    // Parks have their own turf, and two textured slabs on one patch z-fight.
    ...level.parks.map((p) => ({ x: p.x, y: p.y, w: p.w, h: p.h })),
  ];

  // Things a bush must not grow over. The bin and home are what the player is
  // sent to *find*, and a hedge in front of either is a bug rather than
  // scenery; the trees are already standing where they stand.
  //
  // Home's circle covers the whole plot — its half-diagonal, so the corners are
  // in it too. A radius that only reached the pad's edges left bushes growing
  // up through the back of the house now that the house is always drawn.
  const keepOut: KeepOut[] = [
    ...level.bins.map((b) => ({ x: b.x, y: b.y, r: YARDS.OBJECTIVE_CLEARANCE })),
    {
      x: level.home.x + level.home.w / 2,
      y: level.home.y + level.home.h / 2,
      r: Math.hypot(level.home.w, level.home.h) / 2,
    },
    { x: level.start.x, y: level.start.y, r: YARDS.OBJECTIVE_CLEARANCE }, // player start
    ...level.trees.map((t) => ({
      x: t.x,
      y: t.y,
      r: TREES.CANOPY_RADIUS * t.scale * TREES.KINDS[t.kind].canopy,
    })),
  ];

  const planting = new Instances();
  const blocks = blockInteriors(
    level.world,
    level.roads,
    ROADS.WIDTH,
    PAVEMENT.WIDTH,
    YARDS.STREET_MARGIN,
  );

  for (const block of blocks) {
    const patches = freeRects(block, blockers, YARDS.MIN_PATCH);

    for (const patch of patches) {
      const cx = patch.x + patch.w / 2;
      const cy = patch.y + patch.h / 2;
      scene.add(patternedGround(patch.w, patch.h, scrub, YARDS.CELL, cx, YARDS.LIFT, cy));
      for (const p of scatter(patch, YARDS.BUSH_SPACING, YARDS.BUSH_INSET, keepOut)) {
        addBush(planting, p);
      }
    }

    // Weeds go against the things you can actually see the base of: the walls
    // and patios standing in this block, and the block's own edge where the
    // rough ground meets the paved apron. Anything landing off the garden — in
    // a doorway, on a patio, inside the next building along — is dropped.
    const onGarden = (p: Plant): boolean =>
      patches.some((r) => p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h);

    const lines: Plant[] = [
      ...blockers.flatMap((r) =>
        perimeterTufts(r, YARDS.WEED_SPACING, YARDS.WEED_BAND, true, keepOut),
      ),
      ...perimeterTufts(block, YARDS.WEED_SPACING, YARDS.WEED_BAND, false, keepOut),
    ];
    for (const p of lines) if (onGarden(p)) addWeeds(planting, p);
  }

  // Under a named group so tests can tell the planting apart from the town's
  // other instanced batches — the windows and ridge tiles come out of the same
  // kind of collector and are far more numerous.
  const gardens = new THREE.Group();
  gardens.name = GARDEN_GROUP;
  planting.build(gardens);
  scene.add(gardens);
}

/**
 * One bush: a squashed heap of clay balls, shaded crown to underskirt.
 *
 * The shading is the whole reason it reads as a bush rather than a green blob,
 * and it's the same rule the tree canopies and every character in the game
 * follow — from straight above, a single-colour rounded mass has no shape at
 * all. A bush is smaller than a canopy and has fewer pixels to make the point
 * in, so the spread is pushed a little harder.
 *
 * Instanced, keyed by ball size and tone. There are several hundred of these
 * across the town and they are a few pixels each; one `Mesh` apiece would cost
 * more draw calls than the rest of the scenery put together.
 */
function addBush(into: Instances, p: Plant): void {
  const leaf = FOLIAGE.BUSH[Math.floor(hash2(p.x, p.y) * FOLIAGE.BUSH.length) % FOLIAGE.BUSH.length];
  const r = YARDS.BUSH_RADIUS * p.scale;

  for (let i = 0; i < YARDS.BUSH_BLOBS; i++) {
    // One core ball with the rest heaped around and over it.
    const core = i === 0;
    const angle = p.yaw + (i / YARDS.BUSH_BLOBS) * Math.PI * 2 + hash2(p.x + i, p.y) * 1.2;
    const reach = core ? 0 : r * (0.3 + hash2(p.y + i * 3.1, p.x) * 0.32);
    const br = r * (core ? 0.62 : 0.4 + hash2(p.x + i * 5.7, p.y - i) * 0.22);
    const lift = core ? r * 0.34 : r * (0.12 + hash2(p.x - i, p.y + i * 2.3) * 0.34);

    // Shade by height within the bush: sunlit top, shaded skirt.
    //
    // Quantised to three steps, and the radii to whole units, because what
    // costs here is **batches, not instances**. One `InstancedMesh` is emitted
    // per geometry+material pair, so a continuous shade would mint a fresh
    // material for nearly every ball in the town and turn a few dozen draw
    // calls into a few hundred. Three tones per leaf colour is more than enough
    // to give a bush a top and a bottom, which is all the shading is for.
    const step = Math.max(-1, Math.min(1, Math.round(lift / (r * 0.5) - 1)));
    const tone = shadeHex(leaf, step * YARDS.BUSH_SHADE_SPREAD);
    const size = Math.max(1, Math.round(br));
    into.placeScaled(
      `bush:${size}:${tone}`,
      claySphereGeometry(size, 0.55),
      clayMaterial(tone),
      p.x + Math.cos(angle) * reach,
      lift + size * YARDS.BUSH_SQUASH * 0.5,
      p.y + Math.sin(angle) * reach,
      angle,
      1,
      YARDS.BUSH_SQUASH,
      1,
    );
  }
}

/**
 * One tuft of weeds: a few thin blades splaying out of a point.
 *
 * Built once as a merged geometry and then instanced — there are well over a
 * thousand of these, and they are the smallest thing in the game. Merging the
 * blades first means one instance is one tuft rather than four, which is the
 * difference between a batch of 1,200 and a batch of 5,000.
 */
function addWeeds(into: Instances, p: Plant): void {
  const tone =
    FOLIAGE.WEED[Math.floor(hash2(p.x + 3, p.y - 3) * FOLIAGE.WEED.length) % FOLIAGE.WEED.length];
  into.placeScaled(
    `weed:${tone}`,
    weedGeometry(),
    weedMaterial(tone),
    p.x,
    0,
    p.y,
    p.yaw,
    p.scale,
    p.scale,
    p.scale,
  );
}

/**
 * Clay in `tone`, but **double-sided**.
 *
 * A blade is a single plane, and a plane in Three is only drawn from its front.
 * Half of every tuft faces away from the camera, so with the default material
 * half the blades in the town simply aren't there — which is exactly how the
 * first pass shipped, and why the gardens looked bald until you got close.
 *
 * Cached here rather than through `clayMaterial`, whose `opts` path is
 * deliberately uncached (it exists for genuine one-offs); left to that, this
 * would mint a fresh material for every one of a thousand tufts.
 */
const weedMats = new Map<number, THREE.Material>();
function weedMaterial(tone: number): THREE.Material {
  let mat = weedMats.get(tone);
  if (!mat) {
    mat = clayMaterial(tone, { side: THREE.DoubleSide });
    weedMats.set(tone, mat);
  }
  return mat;
}

/**
 * The blades of one weed tuft, merged into a single geometry.
 *
 * Flat planes rather than rounded boxes, and this is the one place in the town
 * that's allowed. `clay.ts`'s rule is that nothing has a hard edge — but a
 * blade of grass at this size is two or three pixels tall, a rounded box of it
 * costs sixteen times the triangles to say nothing, and a blade genuinely is a
 * flat thing. The same argument the window glazing bars won.
 */
let weedGeo: THREE.BufferGeometry | undefined;
function weedGeometry(): THREE.BufferGeometry {
  if (weedGeo) return weedGeo;

  const blades: THREE.BufferGeometry[] = [];
  for (let i = 0; i < YARDS.WEED_BLADES; i++) {
    const h = YARDS.WEED_HEIGHT * (0.7 + hash2(i * 3.7, 1.9) * 0.6);
    const blade = new THREE.PlaneGeometry(YARDS.WEED_THICKNESS, h);
    blade.translate(0, h / 2, 0);
    // Splay outward, and turn each blade about the tuft so it isn't a fan.
    const spin = (i / YARDS.WEED_BLADES) * Math.PI * 2 + hash2(i, 7.1);
    blade.rotateX(YARDS.WEED_LEAN * (0.4 + hash2(i, 11.3)));
    blade.rotateY(spin);
    blades.push(blade.toNonIndexed());
  }
  weedGeo = mergeGeometries(blades) ?? blades[0];
  return weedGeo;
}

/**
 * Trees — taken from the level, built as a rolled clay trunk under a few
 * overlapping balls of foliage.
 *
 * The canopy layout comes from the pure `canopyBlobs()` so it can be tested
 * against the envelope every tree's placement depends on — see
 * systems/trees.ts and the note above `TREES` for why trees are kept off the
 * pavement entirely.
 */
function buildTrees(scene: THREE.Scene): void {
  const bark = patternedClay(TREES.BARK_COLOR, barkTexture(TREES.BARK_COLOR), TREES.BARK_RELIEF);

  for (const t of activeLevel().trees) {
    const group = new THREE.Group();
    const k = TREES.KINDS[t.kind];
    const trunkR = TREES.TRUNK_RADIUS * t.scale * k.girth;
    const trunkH = TREES.TRUNK_HEIGHT * t.scale * k.trunk;
    const canopyR = TREES.CANOPY_RADIUS * t.scale * k.canopy;

    addTrunk(group, trunkR, trunkH, bark);

    // One leaf tone per tree, chosen from its position so a row of trees never
    // repeats in lockstep — three shapes × three tints, and no two neighbours
    // land on the same pair.
    const leaf =
      TREES.LEAF_COLORS[
        Math.floor(hash2(t.x, t.y) * TREES.LEAF_COLORS.length) % TREES.LEAF_COLORS.length
      ];
    // Blob offsets are relative to the top of the trunk.
    const blobs = canopyBlobs(t.x, t.y, canopyR, t.kind);
    // Shade each ball by where it sits in the canopy: sunlit crown, shaded
    // underskirt. See systems/trees.ts → blobShade for why this matters so much
    // at a camera that looks down onto the foliage rather than at it.
    const low = canopyBottom(blobs);
    const high = canopyTop(blobs);
    for (const b of blobs) {
      const tone = shadeHex(leaf, blobShade(b.y, low, high, TREES.LEAF_SHADE_SPREAD));
      group.add(claySphere(b.radius, tone, b.x, trunkH + b.y, b.z));
    }

    group.position.set(t.x, 0, t.y);
    clayWobble(group, t.x, t.y);
    scene.add(group);
  }
}

/**
 * A trunk: a bark-textured shaft standing on a flared base.
 *
 * Two pieces rather than one capsule, and the flare is the one that earns its
 * place. A cylinder meeting the ground dead straight reads as a peg pushed into
 * it; real trunks splay where they land. That splay is also the part of a tree
 * this camera reliably shows, since the base sits lower on screen than the
 * trunk above it and is far less often hidden under the tree's own canopy.
 */
function addTrunk(
  group: THREE.Group,
  radius: number,
  height: number,
  bark: THREE.Material,
): void {
  const flareH = height * TREES.ROOT_FLARE_HEIGHT;

  // The shaft. A cone rather than a cylinder — trunks taper, and the taper is
  // what keeps the canopy from looking balanced on a post.
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.82, radius, height, 10, 1, false),
    bark,
  );
  shaft.position.y = height / 2;
  shaft.castShadow = true;
  shaft.receiveShadow = true;
  group.add(shaft);

  // The flare, spreading out to meet the ground. Sunk slightly so its base is
  // buried rather than meeting the grass in a visible rim.
  const flare = new THREE.Mesh(
    new THREE.CylinderGeometry(radius, radius * TREES.ROOT_FLARE, flareH + 1, 10, 1, false),
    bark,
  );
  flare.position.y = flareH / 2 - 1;
  flare.castShadow = true;
  flare.receiveShadow = true;
  group.add(flare);
}

function buildGround(scene: THREE.Scene): void {
  const level = activeLevel();
  const { width, height } = level.world;

  // A large apron well beyond the playable area. The player can't reach it, but
  // it stops the level ending in a hard edge against the sky — with the fog it
  // reads as the city continuing past the play space. It is out of bounds, so
  // the filter in clay.ts takes the colour out of it.
  scene.add(
    claySlab(width * 4, height * 4, GROUND.SURROUNDS_COLOR, width / 2, -0.5, height / 2),
  );

  // Base surface covering the whole level — the yards and forecourts inside the
  // blocks. The proper footways are laid over this alongside each road (see
  // buildFootways). It takes the same aggregate speckle as the road, in its own
  // colour: it's the second-largest surface in the game and it sits directly
  // against textured paving on one side and coursed brick on the other.
  //
  // The **whole** level, surround included. Shrinking it to `bounds` was how the
  // ring got greyed once, and it left the town beyond the walk standing on the
  // bare apron instead of on its own hardstanding.
  scene.add(
    patternedGround(
      width,
      height,
      patternedClay(
        GROUND.BASE_COLOR,
        tarmacTexture(GROUND.BASE_COLOR),
        GROUND.YARD_RELIEF,
      ),
      GROUND.YARD_CELL,
      width / 2,
      0,
      height / 2,
    ),
  );

  // Grass park patches, raised on a low kerb so they read as planted.
  // The grass goes ON TOP of the kerb block, not inside it — a slab at half the
  // kerb's height would be swallowed by it and the park would render kerb-grey.
  const kerbH = GROUND.PARK_KERB_HEIGHT;
  const turf = patternedClay(GROUND.PARK_COLOR, grassTexture(GROUND.PARK_COLOR), GROUND.GRASS_RELIEF);
  for (const p of level.parks) {
    const [cx, cz] = rectCenter(p);
    const kerb = new THREE.Mesh(
      clayGeometry(p.w + 4, kerbH, p.h + 4),
      clayMaterial(GROUND.PARK_KERB_COLOR),
    );
    kerb.position.set(cx, kerbH / 2, cz);
    kerb.receiveShadow = true;
    scene.add(kerb);
    // Mown stripes rather than a flat green fill — see textures.ts. A park is
    // the largest unbroken area of one colour in the game, and the bands are
    // what stop it reading as a painted panel from overhead.
    scene.add(patternedGround(p.w, p.h, turf, GROUND.GRASS_CELL, cx, kerbH + TURF_LIFT, cz));

    // The loop path round the inside of it — the single strongest signal that
    // this rectangle of grass is a *park* and not a lawn between two terraces,
    // and what the benches and the pillar box are standing on. Laid in the
    // town's own paving, a hair above the turf so the two can't z-fight.
    for (const run of parkPath(p, PARK_FURNITURE.EDGE_INSET, PARK_FURNITURE.PATH_WIDTH)) {
      scene.add(
        patternedGround(
          run.w,
          run.h,
          paving(),
          PAVING_CELL,
          run.x + run.w / 2,
          kerbH + TURF_LIFT * 2,
          run.y + run.h / 2,
        ),
      );
    }
  }
}

/**
 * Whether a patch of tarmac is close enough to the walk to be worth painting.
 *
 * A **cost** rule, not a look one — the surround's colour is handled per fragment
 * (clay.ts → `greyOutsideBounds`) and every street out there is laid in the same
 * tarmac as the town's. But paint is not one mesh: double yellows, drains and
 * centre dashes come to some two thousand two-triangle slabs across a generated
 * ring, each one walked, culled and shadow-tested every frame, for markings a
 * block past the walk and fog-bound behind that. Same trade the ring already
 * makes for houses (`worthDetailing`) and trees (`worthPlanting`).
 *
 * **Overlap, not centre.** Testing the middle of a span is the mistake the
 * greying itself was built on: every through-street crosses the boundary, so a
 * span "outside" by its centre is one the player can be standing on, and the yellow
 * lines would stop dead in the road beside them. Any span that touches the walk
 * at all is painted end to end; only the streets wholly outside go bare, and those
 * are a block out with a terrace in front of them.
 */
function worthMarking(cx: number, cz: number, w: number, d: number): boolean {
  const b = activeLevel().bounds;
  return (
    cx - w / 2 < b.x + b.w &&
    cx + w / 2 > b.x &&
    cz - d / 2 < b.y + b.h &&
    cz + d / 2 > b.y
  );
}

/**
 * Carriageways, drawn as spans *between* junctions.
 *
 * The obvious version — one full-length slab per road — leaves two of them
 * overlapping coplanar over every crossing, which z-fights. Drawing the spans
 * and letting `buildJunctions` own the crossing square means every bit of tarmac
 * is drawn exactly once.
 */
function buildRoads(scene: THREE.Scene): void {
  const level = activeLevel();
  for (const seg of level.roads) {
    if (!isStreet(seg)) continue;
    const horizontal = seg.orient === 'h';
    const [from, to] = roadSpan(seg, level.world);
    // This street's own width for the tarmac and its markings; the crossing
    // streets' widths for where the slab is broken. Once a town has a high
    // street those are different numbers — see `crossingGaps`.
    const width = carriagewayWidth(seg, ROADS.WIDTH);
    const gaps = crossingGaps(seg, level.roads, ROADS.WIDTH, { streetsOnly: true });

    for (const span of subtractGaps(to, gaps, from)) {
      const length = span.to - span.from;
      const mid = span.from + length / 2;
      const cx = horizontal ? mid : seg.pos;
      const cz = horizontal ? seg.pos : mid;

      scene.add(
        patternedGround(
          horizontal ? length : width,
          horizontal ? width : length,
          tarmac(),
          ROADS.AGGREGATE_CELL,
          cx,
          1,
          cz,
        ),
      );

      // This street's own width, not `ROADS.WIDTH` — a high street reaches
      // further across the boundary than a back lane does.
      if (!worthMarking(cx, cz, horizontal ? length : width, horizontal ? width : length)) continue;

      // Double yellows down both kerbs, and the gully drains between them. Flat
      // paint and flat covers: they give the street its character without
      // putting anything on the ground the player can walk into. See
      // systems/markings.ts.
      for (const m of kerbLines(
        seg.orient,
        seg.pos,
        span.from,
        span.to,
        width,
        ROADS.KERB_LINE_INSET,
        ROADS.KERB_LINE_GAP,
        ROADS.KERB_LINE_WIDTH,
      )) {
        scene.add(claySlab(m.w, m.h, ROADS.KERB_LINE_COLOR, m.x, 1.2, m.y));
      }
      for (const at of drainPositions(span.from, span.to, ROADS.DRAIN_SPACING)) {
        for (const side of [-1, 1]) {
          const across = seg.pos + side * (width / 2 - ROADS.DRAIN_SIZE * 0.6);
          const dx = horizontal ? at : across;
          const dz = horizontal ? across : at;
          scene.add(
            claySlab(ROADS.DRAIN_SIZE, ROADS.DRAIN_SIZE * 0.7, ROADS.DRAIN_COLOR, dx, 1.25, dz),
          );
        }
      }

      // Dashed centre line, confined to this span — a real centre line stops at
      // the junction rather than running through the middle of it.
      const step = ROADS.DASH_SPACING;
      const first = Math.ceil((span.from + step / 2) / step) * step;
      for (let d = first; d < span.to - ROADS.DASH_LENGTH / 2; d += step) {
        const mw = horizontal ? ROADS.DASH_LENGTH : 2.5;
        const md = horizontal ? 2.5 : ROADS.DASH_LENGTH;
        scene.add(
          claySlab(mw, md, ROADS.MARKING_COLOR, horizontal ? d : seg.pos, 1.3, horizontal ? seg.pos : d),
        );
      }
    }

    // Extend through-roads out of bounds so the town scenery looks continuous.
    const extent = horizontal ? level.world.width : level.world.height;
    const ext = GROUND.OUT_OF_BOUNDS_EXTENT;
    if (from <= 0) {
      const mid = -ext / 2;
      const cx = horizontal ? mid : seg.pos;
      const cz = horizontal ? seg.pos : mid;
      scene.add(
        patternedGround(
          horizontal ? ext : ROADS.WIDTH,
          horizontal ? ROADS.WIDTH : ext,
          tarmac(),
          ROADS.AGGREGATE_CELL,
          cx,
          1,
          cz,
        ),
      );
    }
    if (to >= extent) {
      const mid = extent + ext / 2;
      const cx = horizontal ? mid : seg.pos;
      const cz = horizontal ? seg.pos : mid;
      scene.add(
        patternedGround(
          horizontal ? ext : ROADS.WIDTH,
          horizontal ? ROADS.WIDTH : ext,
          tarmac(),
          ROADS.AGGREGATE_CELL,
          cx,
          1,
          cz,
        ),
      );
    }
  }
}

/**
 * The junctions themselves: one clean square of tarmac per crossing, plus a stop
 * line on each approach.
 *
 * Stop lines cover only the lane you arrive in (UK: keep left), which is what
 * makes a crossing read as a junction rather than a blank patch of road.
 */
function buildJunctions(scene: THREE.Scene): void {
  const roads = activeLevel().roads;
  for (const j of findJunctions(roads, ROADS.WIDTH)) {
    // `j.w`/`j.h` rather than one size: a junction is a rectangle wherever the
    // two streets crossing it differ in width.
    scene.add(patternedGround(j.w, j.h, tarmac(), ROADS.AGGREGATE_CELL, j.x, 1, j.y));

    // Zebras and stop lines are paint, and cost what the kerb lines cost.
    if (!worthMarking(j.x, j.y, j.w, j.h)) continue;

    for (const seg of roads) {
      const horizontal = seg.orient === 'h';
      // Where this junction sits along the road, and the road's own position.
      const along = horizontal ? j.x : j.y;
      const across = horizontal ? j.y : j.x;
      if ((horizontal ? j.y : j.x) !== seg.pos) continue;

      // A zebra on each approach that exists, set back past the box of tarmac
      // where the two roads meet so it lands where the footway it joins actually
      // is. At a T-junction one arm is missing, and a crossing painted on it
      // would be stripes across somebody's front garden.
      // How wide this street is, and how far the junction box reaches along it —
      // the crossing street's width. Both are needed and they are no longer the
      // same number.
      const width = carriagewayWidth(seg, ROADS.WIDTH);
      const boxHalf = (horizontal ? j.w : j.h) / 2;
      // The setback keeps the margin it always had *beyond the box* rather than
      // a fixed distance from the middle: past a certain width the box itself
      // is deeper than 46 units and the stripes get painted inside the junction.
      const setback = boxHalf + (ROADS.CROSSING_SETBACK - ROADS.WIDTH / 2);

      const approaches = liveApproaches(seg, along, horizontal ? j.w : j.h);
      for (const dir of approaches) {
        for (const m of zebraStripes(
          seg.orient,
          along,
          across,
          width,
          -dir * setback,
          ROADS.CROSSING_STRIPE,
          ROADS.CROSSING_DEPTH,
        )) {
          scene.add(claySlab(m.w, m.h, ROADS.MARKING_COLOR, m.x, 1.28, m.y));
        }
      }

      for (const line of stopLines(
        seg.orient,
        along,
        across,
        width,
        ROADS.STOP_LINE_SETBACK,
        approaches,
        horizontal ? j.w : j.h,
      )) {
        scene.add(
          claySlab(
            line.alongX ? line.length : ROADS.STOP_LINE_WIDTH,
            line.alongX ? ROADS.STOP_LINE_WIDTH : line.length,
            ROADS.MARKING_COLOR,
            line.x,
            1.3,
            line.y,
          ),
        );
      }
    }
  }
}

/**
 * Raised footways down both sides of every road — the bit you're meant to walk
 * the dog along.
 *
 * Each one is a kerb-height box (so the camera's tilt catches a real kerb face
 * dropping to the tarmac) topped with a paving slab and faint joint lines. Strips
 * stop short of any crossroads, which leaves the corners open to cross — see
 * systems/pavement.ts.
 */
function buildFootways(scene: THREE.Scene): void {
  const { WIDTH: pw, KERB_HEIGHT: kerb } = PAVEMENT;

  const level = activeLevel();

  // One kerb-and-paving pair, wherever it lands. Shared between the strips
  // running alongside each road and the caps across the blind ends, so a
  // cul-de-sac head is built out of the same parts as the street leading to it.
  const slab = (cx: number, cz: number, sizeX: number, sizeZ: number): void => {
    // Kerb body — a solid block standing above the road surface.
    const body = new THREE.Mesh(
      clayGeometry(sizeX, kerb, sizeZ),
      clayMaterial(PAVEMENT.KERB_COLOR),
    );
    body.position.set(cx, kerb / 2, cz);
    body.receiveShadow = true;
    scene.add(body);

    // Paving on top, inset a touch so the kerb face stays visible at the edge.
    // The slabs are a *pattern*, not a grid of drawn lines: the footways used
    // to be striped with one thin mesh per joint, which is hundreds of extra
    // draws to say what one canvas says better — the same trade the roofs
    // made when the tiles stopped being geometry.
    scene.add(patternedGround(sizeX - 1, sizeZ - 1, paving(), PAVING_CELL, cx, kerb + 0.1, cz));
  };

  for (const seg of level.roads) {
    if (!isStreet(seg)) continue;
    const horizontal = seg.orient === 'h';
    const [from, to] = roadSpan(seg, level.world);
    // Distance from this street's centreline out to the middle of its footway.
    const offset = carriagewayWidth(seg, ROADS.WIDTH) / 2 + pw / 2;

    // Only one orientation may own the corner squares, or both draw them and
    // they z-fight; vertical footways stop short of the horizontal ones by a
    // full footway width. That is `footwayGapWidth`'s adjustment, applied per
    // crossing so each break is as wide as the street that makes it.
    const grow = seg.orient === 'h' ? 0 : PAVEMENT.WIDTH;

    // Per side: a road that dies at this one breaks the pavement it arrives at
    // and leaves the opposite one running straight through.
    for (const side of [-1, 1] as const) {
      const gaps = crossingGaps(seg, level.roads, ROADS.WIDTH, {
        streetsOnly: true,
        side,
        grow,
      });
      const across = seg.pos + side * offset;

      for (const span of subtractGaps(to, gaps, from)) {
        const length = span.to - span.from;
        const mid = span.from + length / 2;
        const cx = horizontal ? mid : across;
        const cz = horizontal ? across : mid;
        slab(cx, cz, horizontal ? length : pw, horizontal ? pw : length);
      }

      // Extend footways past the map so the town looks continuous. A span that
      // straddles the boundary is drawn once, in the town's own paving — see
      // clay.ts → greyOutsideBounds for what greys the part of it that is out.
      const extent = horizontal ? level.world.width : level.world.height;
      const ext = GROUND.OUT_OF_BOUNDS_EXTENT;
      if (from <= 0) {
        const mid = -ext / 2;
        slab(
          horizontal ? mid : across,
          horizontal ? across : mid,
          horizontal ? ext : pw,
          horizontal ? pw : ext,
        );
      }
      if (to >= extent) {
        const mid = extent + ext / 2;
        slab(
          horizontal ? mid : across,
          horizontal ? across : mid,
          horizontal ? ext : pw,
          horizontal ? pw : ext,
        );
      }
    }
  }

  // The paving across the head of any street that stops mid-town. Without it
  // the two footways end in mid-air facing each other across live tarmac.
  for (const cap of footwayEndCaps(level.world, level.roads, ROADS.WIDTH, PAVEMENT.WIDTH)) {
    slab(cap.x + cap.w / 2, cap.y + cap.h / 2, cap.w, cap.h);
  }

  buildLanes(scene);
}

/**
 * The cobbled pedestrian lanes cutting through the blocks.
 *
 * Laid at the footway's own height and with **no kerb of its own** — there is no
 * carriageway beside a lane to be kerbed off from, and a raised edge where a
 * lane comes out onto a street would be a step across the middle of a route the
 * player walks. The surface is the only thing that changes, which is what makes
 * a lane read as somewhere you walk rather than somewhere you cross.
 */
function buildLanes(scene: THREE.Scene): void {
  const level = activeLevel();
  const bands = laneBands(level.world, level.roads, ROADS.WIDTH, LANES.WIDTH);
  if (bands.length === 0) return;

  const setts = masonryMaterial(LANES.COLOR, cobbleTexture(LANES.COLOR), LANES.RELIEF);
  for (const b of bands) {
    scene.add(
      patternedGround(
        b.w,
        b.h,
        setts,
        COBBLE_CELL,
        b.x + b.w / 2,
        LANES.HEIGHT + 0.1,
        b.y + b.h / 2,
      ),
    );
  }
}

/**
 * The litter bin the player has to find to drop the bag.
 *
 * It was a plain 12-sided cylinder with a flat disc on top — the one hard-edged
 * object in a town whose first rule is that clay can't hold a sharp corner, and
 * unluckily also a thing the player is sent to look for. It's rebuilt here out
 * of the same parts as everything else: a rounded, tapered body on a base, a
 * banded collar, a domed lid, and an aperture you could actually post a bag
 * through.
 *
 * The aperture is the piece that does the work. From this camera you see the top
 * of the bin far more than its sides, so a lid that reads as *open* is what tells
 * you at a glance what the object is for.
 */
function buildBins(scene: THREE.Scene): void {
  for (const bin of activeLevel().bins) buildBin(scene, bin.x, bin.y);
}

/** One bin, at a point. See `buildBins` for why there is more than one. */
function buildBin(scene: THREE.Scene, X: number, Y: number): void {
  const { SIZE, COLOR } = OBJECTIVE.BIN;
  const group = new THREE.Group();
  const r = SIZE / 2;

  // A stone foot, so the bin stands on the pavement rather than in it — the
  // same trick as the cottages' plinth.
  const foot = new THREE.Mesh(
    new THREE.CylinderGeometry(r * 0.92, r * 1.02, 3, 14),
    clayMaterial(OBJECTIVE.BIN_FOOT_COLOR),
  );
  foot.position.y = 1.5;
  foot.receiveShadow = true;
  group.add(foot);

  // The body: tapered, and rolled rather than faceted. A capsule's cap gives the
  // rounded shoulder a hard cylinder can't, and the lower half is buried in the
  // foot so the bin doesn't read as a floating tube.
  const body = clayPill(r * 0.94, SIZE * 0.62, COLOR);
  body.position.y = SIZE * 0.44;
  group.add(body);

  // Vertical ribs, the pressed seams of a real municipal bin. Cheap, and the
  // only thing stopping the body being a smooth blank cylinder from above.
  const ribGeo = clayGeometry(1.6, SIZE * 0.5, 1.6);
  const ribMat = clayMaterial(shadeHex(COLOR, -0.16));
  for (let i = 0; i < OBJECTIVE.BIN_RIBS; i++) {
    const a = (i / OBJECTIVE.BIN_RIBS) * Math.PI * 2;
    const rib = new THREE.Mesh(ribGeo, ribMat);
    rib.position.set(Math.cos(a) * r * 0.92, SIZE * 0.42, Math.sin(a) * r * 0.92);
    rib.rotation.y = -a;
    group.add(rib);
  }

  // A banded collar under the lid, catching its own line of light — the same
  // job the cottages' string course does.
  const collar = new THREE.Mesh(
    new THREE.CylinderGeometry(r * 1.04, r * 1.04, 3.5, 14),
    clayMaterial(shadeHex(COLOR, 0.18)),
  );
  collar.position.y = SIZE * 0.74;
  collar.castShadow = true;
  group.add(collar);

  // Domed lid, oversailing the collar so it throws a shadow line round the bin.
  // Shallow: a tall dome turns the whole thing into a mushroom, which is exactly
  // what the first attempt looked like.
  const lidR = r * 1.06;
  const lidRise = 0.3;
  const lidY = SIZE * 0.78;
  const lid = new THREE.Mesh(
    new THREE.SphereGeometry(lidR, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
    clayMaterial(OBJECTIVE.BIN_LID_COLOR),
  );
  lid.scale.y = lidRise;
  lid.position.y = lidY;
  lid.castShadow = true;
  group.add(lid);

  // The mouth: a dark slot let into the crown of the lid, and the thing that
  // says "post rubbish here" from directly overhead — which is the angle this
  // camera almost always sees a bin from. It has to sit *on* the crown; sunk
  // any lower it is simply inside a solid dome and invisible.
  const mouth = new THREE.Mesh(
    clayGeometry(lidR * 0.92, 3, lidR * 0.44),
    clayMaterial(OBJECTIVE.BIN_MOUTH_COLOR),
  );
  mouth.position.y = lidY + lidR * lidRise - 0.6;
  group.add(mouth);

  group.position.set(X, 0, Y);
  clayWobble(group, X, Y);
  scene.add(group);
}

/**
 * The walker's own house, standing at the back of the home plot.
 *
 * Part of the world, built with everything else and visible from the first
 * frame — the player is looking straight at it as they set off, because they
 * set off from its doorstep. It used to be built inside the hidden win-zone
 * group instead, so the house you had supposedly just walked out of popped into
 * existence two minutes later when the last objective flipped.
 */
function buildHome(scene: THREE.Scene): void {
  buildCottage(scene, homeHouseRect(activeLevel().home));
}

/**
 * The win-zone glow over the home plot — hidden until the bag is binned.
 * Returns it for toggling.
 *
 * Only the glow: the house itself is `buildHome` above, and permanent. This is
 * the *trigger* made visible, which is a thing to reveal at the end, and the
 * distinction is the whole reason they are two functions.
 */
export function createHomeZone(scene: THREE.Scene): THREE.Group {
  const { COLOR } = OBJECTIVE.HOME;
  // The level stores the plot as a corner-anchored rect; everything drawn here
  // is centred, so take the centre once.
  const home = activeLevel().home;
  const [X, Y] = rectCenter(home);
  const { w: WIDTH, h: HEIGHT } = home;
  const group = new THREE.Group();

  const pad = claySlab(WIDTH, HEIGHT, COLOR, X, 1.6, Y, true); // own material: faded below
  (pad.material as THREE.MeshStandardMaterial).transparent = true;
  (pad.material as THREE.MeshStandardMaterial).opacity = 0.55;
  group.add(pad);

  group.visible = false;
  scene.add(group);
  return group;
}

/** A pulsing ring waypoint ("go here"). Hidden by default. */
export function createMarker(
  scene: THREE.Scene,
  x: number,
  y: number,
  color: number,
  /**
   * Outer radius. The default is the waypoint size every marker used to be —
   * a "here" ring, drawn the same size whatever it is pointing at. Pass the
   * real one where the ring *is* the trigger zone (the cleanup ring does), so
   * that standing in it and being in range are the same thing.
   */
  radius = 32,
): THREE.Mesh {
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(radius - 6, radius, 28),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85, side: THREE.DoubleSide }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.set(x, 2, y);
  ring.visible = false;
  scene.add(ring);
  return ring;
}

/** Animate a marker ring's pulse. `t` is elapsed seconds. */
export function pulseMarker(ring: THREE.Mesh, t: number): void {
  if (!ring.visible) return;
  const s = 0.85 + Math.sin(t * 3) * 0.18;
  ring.scale.set(s, s, s);
  (ring.material as THREE.MeshBasicMaterial).opacity = 0.55 + Math.sin(t * 3) * 0.3;
}
